import { useEffect, useMemo, useRef, useState } from 'react';

import { ArrowUpIcon, TargetIcon, XIcon } from '@phosphor-icons/react';

import type { SkillItem } from '@pages/PracticePage/model';

import { cn } from '@shared/lib/utils';
import { Button } from '@shared/ui/button';

import { buildTheorySections } from './practiceTheoryContent';
import { getScoreColor } from '../lib/getScoreColor';

interface PracticeTheoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: SkillItem | null;
  groupTitle?: string;
  onOpenChat: (message: string) => void;
}

export const PracticeTheoryModal = ({
  isOpen,
  onClose,
  item,
  groupTitle,
  onOpenChat,
}: PracticeTheoryModalProps) => {
  const sections = useMemo(() => buildTheorySections(), []);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (!isOpen) return;
    setActiveSectionId(sections[0]?.id ?? null);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0 });
    }
  }, [isOpen, sections]);

  useEffect(() => {
    if (isOpen) {
      setChatMessage('');
    }
  }, [isOpen, item]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    let frame = 0;
    const updateActiveSection = () => {
      const containerTop = container.getBoundingClientRect().top;
      const scrollTop = container.scrollTop;
      const offset = 160;
      let nextId = sections[0]?.id ?? null;

      sections.forEach((section) => {
        const element = sectionRefs.current[section.id];
        if (!element) return;
        const elementTop = element.getBoundingClientRect().top - containerTop + scrollTop;
        if (elementTop <= scrollTop + offset) {
          nextId = section.id;
        }
      });

      if (nextId) {
        setActiveSectionId(nextId);
      }
    };

    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        updateActiveSection();
        frame = 0;
      });
    };

    updateActiveSection();
    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, sections]);

  if (!isOpen || !item) return null;

  const handleSectionClick = (id: string) => {
    const target = sectionRefs.current[id];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleChatSubmit = () => {
    if (!item) return;
    const trimmed = chatMessage.trim();
    if (!trimmed) return;
    const topicLabel = groupTitle ? `${item.title} (${groupTitle})` : item.title;
    onOpenChat(`Topic: ${topicLabel}\nQuestion: ${trimmed}`);
    setChatMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-100">
      <div className="fixed inset-0 bg-black/40" />
      <div
        ref={scrollContainerRef}
        className="
          relative z-10 flex size-full items-start justify-center overflow-y-auto p-4
          [-ms-overflow-style:none] [scrollbar-width:none]
          [&::-webkit-scrollbar]:size-0
        "
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className="
            relative flex min-h-[90vh] w-full max-w-280 flex-col rounded-3xl bg-white shadow-2xl
          "
        >
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3">
              <span className={cn('text-xl font-medium', getScoreColor(item.score))}>
                {item.score}%
              </span>
              <span className="text-lg font-medium text-foreground">{item.title}</span>
            </div>

            <div className="flex items-center gap-2">
              <Button type="button" size="sm" className="rounded-sm px-4">
                <TargetIcon size={25} weight="duotone" />
                Start Targeted Practice
              </Button>
              <button
                type="button"
                onClick={onClose}
                className="
                  rounded-full p-2 text-muted-foreground transition-colors
                  hover:bg-accent hover:text-foreground
                "
                aria-label="Close"
              >
                <XIcon className="size-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 flex-col p-1.5">
            <div
              className="
                flex flex-1 flex-col gap-6 rounded-2xl border border-border/60 bg-accent p-6
              "
            >
              <div className="flex flex-1 gap-6 pb-24">
                <div className="flex-1 pr-6">
                  <div className="text-xs font-medium text-muted-foreground uppercase">
                    {groupTitle ? `Practice / ${groupTitle}` : 'Practice'}
                  </div>

                  {sections.map((section, index) => (
                    <section
                      key={section.id}
                      id={section.id}
                      ref={(node) => {
                        sectionRefs.current[section.id] = node;
                      }}
                      className={cn('scroll-mt-24', index === 0 ? 'mt-4' : 'mt-8')}
                    >
                      <h3 className="text-xl font-medium text-foreground">{section.title}</h3>
                      <div className="mt-3 space-y-3 text-sm/6 text-muted-foreground">
                        {section.content}
                      </div>
                    </section>
                  ))}
                </div>

                <div className="sticky top-6 w-56 shrink-0 self-start border-border/60 pl-6">
                  <div className="text-xs font-medium text-muted-foreground uppercase">
                    Contents
                  </div>
                  <div className="mt-3 flex flex-col gap-1">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => {
                          handleSectionClick(section.id);
                        }}
                        className={cn(
                          'rounded-xl px-3 py-2 text-left text-sm transition-colors',
                          activeSectionId === section.id
                            ? 'bg-background-4'
                            : `
                              text-muted-foreground
                              hover:bg-background hover:text-foreground
                            `
                        )}
                      >
                        {section.title}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="sticky bottom-4 z-10 flex justify-center">
                <div
                  className="
                    flex w-full max-w-lg items-center gap-3 rounded-full border border-border/60
                    bg-background-0 px-4 py-3 shadow-sm
                  "
                >
                  <input
                    type="text"
                    value={chatMessage}
                    onChange={(event) => {
                      setChatMessage(event.target.value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleChatSubmit();
                      }
                    }}
                    placeholder="Ask Cortexa AI about this topic..."
                    className="
                      flex-1 bg-transparent text-sm text-foreground outline-none
                      placeholder:text-muted-foreground
                    "
                  />
                  <button
                    type="button"
                    onClick={handleChatSubmit}
                    disabled={!chatMessage.trim()}
                    className="
                      flex size-9 items-center justify-center rounded-full bg-primary
                      text-primary-foreground shadow-btn transition-colors
                      hover:bg-primary-hover
                      disabled:cursor-not-allowed disabled:opacity-50
                    "
                    aria-label="Send message"
                  >
                    <ArrowUpIcon className="size-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
